/**
 * Audit Event Categories and Configuration
 * 
 * ⚠️  AUTO-GENERATED FILE - DO NOT EDIT DIRECTLY
 * 
 * This file is automatically generated from api/utils/AuditEventDescriptions.ts
 * Run 'npm run generate:audit-categories' to regenerate
 * 
 * Last generated: 2026-07-13T22:50:52.202Z
 */

export const EVENT_CATEGORIES = {
  AUTHENTICATION: {
    name: 'Authentication & Sessions',
    icon: 'icon-key',
    color: 'text-primary',
    events: [
      'API_TOKEN_EXPIRED',
      'API_TOKEN_GENERATED',
      'API_TOKEN_PURGED',
      'API_TOKEN_REVOKED',
      'API_TOKEN_ROTATED',
      'JWT_EXCHANGE_FAILED',
      'JWT_EXCHANGE_SUCCESS',
      'LOGIN',
      'LOGIN_FAILED',
      'LOGOUT',
      'PASSWORD_CHANGED',
      'PASSWORD_RESET_COMPLETED',
      'PASSWORD_RESET_REQUESTED',
      'PASSWORD_SET',
      'PASSWORD_SETUP_INITIATED',
      'SESSIONS_REVOKED',
    ]
  },
  ACCESS_CONTROL: {
    name: 'Access Control',
    icon: 'icon-shield',
    color: 'text-error',
    events: [
      'PERMISSION_DENIED',
    ]
  },
  USER_LIFECYCLE: {
    name: 'User Lifecycle',
    icon: 'icon-users',
    color: 'text-info',
    events: [
      'ACCOUNT_LOCKED',
      'ACCOUNT_UNLOCKED',
      'EMAIL_VERIFIED',
      'JIT_USER_PROVISIONED',
      'JIT_USER_REJECTED_DOMAIN',
    ]
  },
  MFA: {
    name: 'Multi-Factor Authentication',
    icon: 'icon-shield',
    color: 'text-warning',
    events: [
      'MFA_CHALLENGE',
      'MFA_DISABLED',
      'MFA_FAILED',
      'MFA_SETUP',
      'MFA_SUCCESS',
    ]
  },
  ROLE_MANAGEMENT: {
    name: 'Role Management',
    icon: 'icon-users',
    color: 'text-info',
    events: [
      'ROLE_ASSIGNED',
      'ROLE_REVOKED',
    ]
  },
  IDENTITY_PROVIDER: {
    name: 'Identity Provider',
    icon: 'icon-vpn_key',
    color: 'text-accent',
    events: [
      'IDP_CREATED',
      'IDP_DEFAULT_REPLACED',
      'IDP_DELETED',
      'IDP_DISABLED',
      'IDP_ENABLED',
      'IDP_UPDATED',
    ]
  },
  CONFIGURATION: {
    name: 'Configuration',
    icon: 'icon-metalark-settings',
    color: 'text-base-content',
    events: [
      'INTEGRATION_DISABLED',
      'INTEGRATION_ENABLED',
      'SECURITY_CONFIG_RESET_TO_DEFAULTS',
      'SECURITY_CONFIG_UPDATED',
    ]
  },
  CAMPAIGN: {
    name: 'Campaigns',
    icon: 'icon-notification',
    color: 'text-warning',
    events: [
      'CAMPAIGN_ACTIVATED',
      'CAMPAIGN_ARCHIVED',
      'CAMPAIGN_CREATED',
      'CAMPAIGN_PAUSED',
      'CAMPAIGN_UPDATED',
    ]
  },
  WEBHOOKS: {
    name: 'Webhooks',
    icon: 'icon-link',
    color: 'text-accent',
    events: [
      'WEBHOOK_AUTO_DISABLED',
      'WEBHOOK_HMAC_ROTATED',
      'WEBHOOK_RULE_CREATED',
      'WEBHOOK_RULE_DELETED',
      'WEBHOOK_RULE_UPDATED',
      'WEBHOOK_SUBSCRIPTION_CREATED',
      'WEBHOOK_SUBSCRIPTION_DELETED',
      'WEBHOOK_SUBSCRIPTION_UPDATED',
      'WEBHOOK_TEST_FIRED',
      'WEBHOOK_URL_ROTATED',
    ]
  },
  COMPLIANCE: {
    name: 'Compliance (GDPR/CCPA)',
    icon: 'icon-file-text',
    color: 'text-info',
    events: [
      'DATA_DELETION_COMPLETED',
      'EULA_ACCEPTED',
    ]
  },
  IMPERSONATION: {
    name: 'Impersonation',
    icon: 'icon-user-tie',
    color: 'text-warning',
    events: [
      'IMPERSONATION_ENDED',
      'IMPERSONATION_STARTED',
    ]
  },
  WORKSPACE_ACCESS: {
    name: 'Workspace Access',
    icon: 'icon-enter',
    color: 'text-info',
    events: [
      'WORKSPACE_ACCESS_ENDED',
      'WORKSPACE_ACCESS_RENEWED',
      'WORKSPACE_ACCESS_STARTED',
    ]
  },
  PASSKEY: {
    name: 'Passkey Management',
    icon: 'icon-metalark-fingerprint',
    color: 'text-accent',
    events: [
      'PASSKEY_NICKNAME_CHANGED',
      'PASSKEY_REGISTERED',
      'PASSKEY_REVOKED',
      'PASSKEY_REVOKED_BY_ADMIN',
      'PASSKEY_USED',
    ]
  },
};

/**
 * Event types visible to SYSTEM_ADMIN only. Derived from the
 * `systemAdminOnly` flag in api/utils/AuditEventDescriptions.ts;
 * the backend enforces the same restriction on audit-log queries.
 */
export const SYSTEM_ADMIN_ONLY_EVENTS = [
  'CAMPAIGN_ACTIVATED',
  'CAMPAIGN_ARCHIVED',
  'CAMPAIGN_CREATED',
  'CAMPAIGN_PAUSED',
  'CAMPAIGN_REDEEMED',
  'CAMPAIGN_UPDATED',
];

/**
 * Get category for an event type
 */
export function getCategoryForEvent(eventType) {
  for (const [categoryKey, category] of Object.entries(EVENT_CATEGORIES)) {
    if (category.events.includes(eventType)) {
      return { key: categoryKey, ...category };
    }
  }
  return null;
}

/**
 * Event type icons with status-specific styling
 */
export const EVENT_ICONS = {
  // Access Control
  'ACCESS_DENIED': 'icon-blocked text-error',

  // User Lifecycle
  'ACCOUNT_LOCKED': 'icon-lock text-error',
  'ACCOUNT_UNLOCKED': 'icon-unlocked text-success',

  // Authentication & Sessions
  'API_TOKEN_EXPIRED': 'icon-key text-success',
  'API_TOKEN_GENERATED': 'icon-key text-success',
  'API_TOKEN_PURGED': 'icon-key text-success',
  'API_TOKEN_REVOKED': 'icon-key text-success',
  'API_TOKEN_ROTATED': 'icon-key text-success',

  // Campaigns
  'CAMPAIGN_ACTIVATED': 'icon-notification text-success',
  'CAMPAIGN_ARCHIVED': 'icon-notification text-success',
  'CAMPAIGN_CREATED': 'icon-notification text-success',
  'CAMPAIGN_PAUSED': 'icon-notification text-success',
  'CAMPAIGN_REDEEMED': 'icon-notification text-success',
  'CAMPAIGN_UPDATED': 'icon-notification text-success',

  // Compliance (GDPR/CCPA)
  'CONSENT_GRANTED': 'icon-checkbox-checked text-success',
  'CONSENT_REVOKED': 'icon-checkbox-unchecked text-warning',
  'DATA_DELETION_COMPLETED': 'icon-bin text-warning',
  'DATA_DELETION_REQUESTED': 'icon-bin text-warning',

  // Data & Privacy
  'DATA_EXPORTED': 'icon-download text-info',

  // User Lifecycle
  'EMAIL_VERIFICATION_SENT': 'icon-mail text-success',
  'EMAIL_VERIFIED': 'icon-mail text-success',

  // Compliance (GDPR/CCPA)
  'EULA_ACCEPTED': 'icon-checkbox-checked text-success',

  // Identity Provider
  'IDP_CREATED': 'icon-vpn_key text-success',
  'IDP_DEFAULT_REPLACED': 'icon-vpn_key text-success',
  'IDP_DELETED': 'icon-vpn_key text-warning',
  'IDP_DISABLED': 'icon-vpn_key text-warning',
  'IDP_ENABLED': 'icon-vpn_key text-success',
  'IDP_UPDATED': 'icon-vpn_key text-success',

  // Impersonation
  'IMPERSONATION_ENDED': 'icon-user-tie text-success',
  'IMPERSONATION_STARTED': 'icon-user-tie text-warning',

  // Configuration
  'INTEGRATION_DISABLED': 'icon-link text-success',
  'INTEGRATION_ENABLED': 'icon-link text-success',

  // User Lifecycle
  'JIT_USER_PROVISIONED': 'icon-user-plus text-success',
  'JIT_USER_REJECTED_DOMAIN': 'icon-blocked text-error',

  // Authentication & Sessions
  'JWT_EXCHANGE_FAILED': 'icon-cancel-circle text-error',
  'JWT_EXCHANGE_SUCCESS': 'icon-key text-success',
  'LOGIN': 'icon-enter text-success',
  'LOGIN_FAILED': 'icon-cancel-circle text-error',
  'LOGOUT': 'icon-metalark-logout text-info',

  // Multi-Factor Authentication
  'MFA_CHALLENGE': 'icon-shield text-success',
  'MFA_DISABLED': 'icon-shield text-warning',
  'MFA_FAILED': 'icon-shield text-warning',
  'MFA_SETUP': 'icon-shield text-success',
  'MFA_SUCCESS': 'icon-shield text-success',

  // Passkey Management
  'PASSKEY_NICKNAME_CHANGED': 'icon-metalark-fingerprint text-success',
  'PASSKEY_REGISTERED': 'icon-metalark-fingerprint text-success',
  'PASSKEY_REVOKED': 'icon-metalark-fingerprint text-success',
  'PASSKEY_REVOKED_BY_ADMIN': 'icon-metalark-fingerprint text-warning',
  'PASSKEY_USED': 'icon-metalark-fingerprint text-success',

  // Authentication & Sessions
  'PASSWORD_CHANGED': 'icon-lock text-success',
  'PASSWORD_RESET_COMPLETED': 'icon-lock text-success',
  'PASSWORD_RESET_REQUESTED': 'icon-lock text-success',
  'PASSWORD_SET': 'icon-lock text-success',
  'PASSWORD_SETUP_INITIATED': 'icon-lock text-success',

  // Access Control
  'PERMISSION_DENIED': 'icon-blocked text-error',
  'RATE_LIMIT_EXCEEDED': 'icon-warning text-warning',

  // Data & Privacy
  'REPORT_GENERATED': 'icon-file-text text-info',

  // Role Management
  'ROLE_ASSIGNED': 'icon-user-plus text-success',
  'ROLE_REVOKED': 'icon-user-minus text-error',

  // Configuration
  'SECURITY_CONFIG_RESET_TO_DEFAULTS': 'icon-refresh text-info',
  'SECURITY_CONFIG_UPDATED': 'icon-settings text-info',
  'SECURITY_SETTINGS_CHANGED': 'icon-metalark-settings text-warning',

  // Data & Privacy
  'SENSITIVE_DATA_ACCESSED': 'icon-database text-success',

  // Authentication & Sessions
  'SESSION_EXPIRED': 'icon-clock text-success',
  'SESSIONS_REVOKED': 'icon-metalark-log-out text-warning',

  // User Lifecycle
  'USER_CREATED': 'icon-user-plus text-success',
  'USER_DELETED': 'icon-user-minus text-error',
  'USER_INVITED': 'icon-mail text-info',
  'USER_REACTIVATED': 'icon-user-check text-success',
  'USER_SUSPENDED': 'icon-user-minus text-warning',
  'USER_UPDATED': 'icon-user-check text-success',

  // Webhooks
  'WEBHOOK_AUTO_DISABLED': 'icon-warning text-warning',
  'WEBHOOK_HMAC_ROTATED': 'icon-key text-info',
  'WEBHOOK_RULE_CREATED': 'icon-checkbox-checked text-success',
  'WEBHOOK_RULE_DELETED': 'icon-cancel-circle text-warning',
  'WEBHOOK_RULE_UPDATED': 'icon-metalark-settings text-info',
  'WEBHOOK_SUBSCRIPTION_CREATED': 'icon-link text-success',
  'WEBHOOK_SUBSCRIPTION_DELETED': 'icon-link text-warning',
  'WEBHOOK_SUBSCRIPTION_UPDATED': 'icon-link text-info',
  'WEBHOOK_TEST_FIRED': 'icon-notification text-info',
  'WEBHOOK_URL_ROTATED': 'icon-refresh text-info',

  // Workspace Access
  'WORKSPACE_ACCESS_ENDED': 'icon-metalark-logout text-info',
  'WORKSPACE_ACCESS_RENEWED': 'icon-refresh text-info',
  'WORKSPACE_ACCESS_STARTED': 'icon-enter text-info',
};

/**
 * Format event type for display
 */
export function formatEventType(eventType) {
  if (!eventType) return '-';
  return eventType.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
}

/**
 * Get icon class for event type
 */
export function getEventIcon(eventType, size = '') {
  const sizeClass = size ? ` ${size}` : '';
  return (EVENT_ICONS[eventType] || 'icon-notification text-gray-500') + sizeClass;
}

/**
 * Get all events grouped by category
 */
export function getEventsByCategory() {
  return Object.entries(EVENT_CATEGORIES).map(([key, category]) => ({
    key,
    name: category.name,
    icon: category.icon,
    color: category.color,
    events: category.events
  }));
}
